// pages/R10Cockpit.tsx — R10 realtime trading cockpit (read-only snapshot view).
//
// Rebuilt 2026-07-04 after the dist-only bundle page was lost with
// frontend/dist. Contract source: backend/fastapi_pkg/_r10_cockpit.py —
// GET /api/r10/cockpit/snapshot assembles every block best-effort (blocks
// are always PRESENT, individual values may be null → render N/A), and
// /ws/r10/cockpit pushes {sequence, snapshot} frames. This page polls at
// 8s and applies WS frames when the socket is up (frames win only if
// their sequence is newer); the socket closes with 4401 when the session
// cookie is absent, so polling must always stay alive as the fallback.
//
// Columns the provider currently hardcodes to null on every row
// (turnover_rate, vwap_distance_pct, liquidity_grade,
// position_size_preview, sell_score, hazard_rate, exit_score,
// intraday_drawdown_pct) and the empty session_metrics groups are NOT
// rendered — re-add them when the server starts filling those planes.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n, type Locale } from '../i18n';
import { fmtAge as fmtAgeAt } from '../core/timefmt';
import { safeGet } from '../api/safeFetch';
import { ExtendedHoursPanel } from '../components/market/ExtendedHoursPanel';

interface SessionStatus {
  feed_age_ms: number | null;
  score_age_ms: number | null;
  market_session: string;
  runtime_state: string;
  scoring_freeze_state: string;
  live_submit_allowed: boolean;
  market_data_source: string;
  score_source: string;
  reason_code: string;
  blockers: string[];
}

interface RuntimeGate {
  runtime_state: string;
  score_feed_state: string;
  data_health_state: string;
  desired_mode: string;
  boot_policy_state: string;
  reason_code: string;
  runtime_session_id: string;
  live_submit_allowed: boolean;
  buy_intent_allowed: boolean;
  sell_eval_allowed: boolean;
  sell_submit_allowed: boolean;
}

interface BuyRow {
  rank: number;
  symbol: string;
  buy_score: number | null;
  price: number | null;
  change_pct: number | null;
  gap_pct: number | null;
  relative_volume: number | null;
  buy_state: string;
  block_reason: string;
}

interface SellRow {
  rank: number;
  symbol: string;
  position_qty: number | null;
  broker_available_qty: number | null;
  avg_cost: number | null;
  last_price: number | null;
  unrealized_pnl: number | null;
  unrealized_pnl_pct: number | null;
  sell_state: string;
}

export interface Snapshot {
  sequence: number;
  as_of: number;
  market_data_source: string;
  score_source: string;
  broker_truth_source: string;
  session_status: SessionStatus;
  runtime_gate: RuntimeGate;
  counts: { quotes: number; buy_rankings: number; sell_rankings: number };
  thresholds: { buy_score_threshold: number | null };
  buy_rankings: BuyRow[];
  sell_rankings: SellRow[];
  position_risk: {
    mode: string;
    position_count: number;
    liquidity_warning: string | null;
    overnight_position_risk: string | null;
  };
  broker_meta: { source: string; last_sync_at: number | string | null };
}

const REFRESH_MS = 8000;
const FETCH_TIMEOUT_MS = 12000;
const CACHE_KEY = 'r10Cockpit:lastSnapshot:v1';
const WS_RETRY_MS = 15000;

function tr(locale: Locale, en: string, zh: string) {
  return locale === 'zh' ? zh : en;
}
/** State strings from the API are contractually non-null ("NONE" default),
 * but guard anyway — the old bundle's unguarded badge classifier crashing
 * on a null state is exactly the failure mode this page must not repeat. */
function st(value: unknown): string {
  const text = value == null ? '' : String(value).trim();
  return text || 'NONE';
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function fmtNum(value: number | null | undefined, digits = 2): string {
  const n = num(value);
  return n === null ? 'N/A' : n.toFixed(digits);
}

function fmtPct(value: number | null | undefined): string {
  const n = num(value);
  return n === null ? 'N/A' : `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function fmtAge(ms: number | null | undefined, locale: Locale): string {
  const n = num(ms);
  if (n === null) return 'N/A';
  if (n < 1000) return `${Math.round(n)}ms`;
  if (n < 120_000) return `${(n / 1000).toFixed(1)}s`;
  return tr(locale, `${Math.round(n / 60_000)}m`, `${Math.round(n / 60_000)}分`);
}

function fmtSyncTs(
  raw: number | string | null | undefined,
  pageNowMs: number,
  locale: Locale,
): string {
  // Freshness is expressed relative to the single page clock (lastFetch);
  // absolute wall-clock renders would just be a second competing "now".
  return fmtAgeAt(raw, pageNowMs || Date.now(), locale);
}

const GOOD_STATES = new Set([
  'LIVE',
  'OPEN',
  'ACTIVE',
  'READY',
  'HEALTHY',
  'FRESH',
  'ELIGIBLE',
  'SCORING_ACTIVE',
  'LIVE_AUTONOMOUS_ACTIVE',
  'CANDIDATE',
]);
const WARN_STATES = new Set(['FROZEN_SOAK', 'SCORING_FROZEN', 'PREMARKET', 'POSTMARKET', 'BOOT_HOLD', 'WATCH', 'HOLD', 'ARMED_BLOCKED', 'SAFE_SOAK']);
const BAD_STATES = new Set(['STALE', 'ERROR', 'FAILED', 'BLOCKED', 'DEGRADED']);

function stateTone(value: string): string {
  const s = st(value).toUpperCase();
  if (GOOD_STATES.has(s)) return 'bg-green-900 text-green-300 border border-green-700';
  if (WARN_STATES.has(s)) return 'bg-amber-950 text-amber-300 border border-amber-700';
  if (BAD_STATES.has(s)) return 'bg-red-950 text-red-300 border border-red-700';
  if (s === 'CLOSED') return 'bg-gray-800 text-gray-300 border border-gray-600';
  return 'bg-gray-800 text-gray-400 border border-gray-700';
}

function selectPriorityBuyRows(rows: BuyRow[], limit = 10): BuyRow[] {
  const seen = new Set<string>();
  return [
    ...rows.filter(row => st(row.block_reason).toUpperCase() === 'NONE'),
    ...rows,
  ]
    .filter(row => {
      const key = `${row.rank}:${row.symbol}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit)
    .sort((left, right) => left.rank - right.rank);
}

function Badge({ label, value }: { label: string; value: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${stateTone(value)}`}>
      <span className="opacity-70 font-normal">{label}</span>
      {st(value)}
    </span>
  );
}

function BoolBadge({ label, value, locale }: { label: string; value: boolean; locale: Locale }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${
      value ? 'bg-green-900 text-green-300 border border-green-700' : 'bg-gray-800 text-gray-400 border border-gray-700'
    }`}>
      <span className="opacity-70 font-normal">{label}</span>
      {value ? tr(locale, 'YES', '允许') : tr(locale, 'NO', '禁止')}
    </span>
  );
}

function readCache(): Snapshot | null {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Snapshot) : null;
  } catch {
    return null;
  }
}

function writeCache(snap: Snapshot) {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(snap));
  } catch {
    // Ignore storage failures; the live API stays authoritative.
  }
}

export function R10Cockpit({ initialSnapshot }: { initialSnapshot?: Snapshot } = {}) {
  const { locale } = useI18n();
  const initial = useRef<Snapshot | null>(initialSnapshot ?? readCache());
  const [snap, setSnap] = useState<Snapshot | null>(initial.current);
  const [loading, setLoading] = useState(!initial.current);
  const [error, setError] = useState('');
  const [lastFetch, setLastFetch] = useState(initial.current ? Date.now() : 0);
  const [wsLive, setWsLive] = useState(false);
  const snapRef = useRef<Snapshot | null>(initial.current);
  const abortRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);

  const apply = useCallback((next: Snapshot) => {
    // WS frames and poll responses race: newest sequence wins.
    const prev = snapRef.current;
    if (prev && num(next?.sequence) !== null && next.sequence < prev.sequence) return;
    snapRef.current = next;
    writeCache(next);
    setSnap(next);
    setError('');
  }, []);

  const fetchSnapshot = useCallback(async () => {
    if (inFlightRef.current) return;
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    inFlightRef.current = true;
    const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const next = await safeGet<Snapshot>('/api/r10/cockpit/snapshot', {
        cache: 'no-store',
        signal: controller.signal,
      });
      apply(next);
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        setError(e?.message || tr(locale, 'Request failed', '请求失败'));
      } else if (!snapRef.current) {
        setError(tr(locale, 'Load timed out', '加载超时'));
      }
    } finally {
      window.clearTimeout(timer);
      inFlightRef.current = false;
      setLoading(false);
      setLastFetch(Date.now());
    }
  }, [apply, locale]);

  // Poll loop — always on; WS only makes updates arrive sooner.
  useEffect(() => {
    fetchSnapshot();
    const id = window.setInterval(fetchSnapshot, REFRESH_MS);
    return () => {
      window.clearInterval(id);
      abortRef.current?.abort();
    };
  }, [fetchSnapshot]);

  // WS enhancement: {sequence, snapshot} frames; polling remains authoritative.
  useEffect(() => {
    let ws: WebSocket | null = null;
    let retryTimer = 0;
    let disposed = false;
    const connect = () => {
      if (disposed) return;
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      try {
        ws = new WebSocket(`${proto}://${window.location.host}/ws/r10/cockpit`);
      } catch {
        retryTimer = window.setTimeout(connect, WS_RETRY_MS);
        return;
      }
      ws.onopen = () => setWsLive(true);
      ws.onmessage = ev => {
        try {
          const frame = JSON.parse(ev.data);
          if (frame && typeof frame === 'object' && frame.snapshot) {
            apply(frame.snapshot as Snapshot);
          }
        } catch {
          // Malformed frame: ignore; polling remains authoritative.
        }
      };
      ws.onclose = () => {
        setWsLive(false);
        if (!disposed) retryTimer = window.setTimeout(connect, WS_RETRY_MS);
      };
      ws.onerror = () => ws?.close();
    };
    connect();
    return () => {
      disposed = true;
      window.clearTimeout(retryTimer);
      ws?.close();
    };
  }, [apply]);

  if (loading && !snap) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-400 flex items-center justify-center">
        {tr(locale, 'Loading R10 cockpit...', '正在加载 R10 驾驶舱...')}
      </div>
    );
  }
  if (!snap) {
    return (
      <div className="min-h-screen bg-gray-950 text-red-400 flex items-center justify-center">
        {error || tr(locale, 'No snapshot', '暂无快照')}
      </div>
    );
  }

  const ss = snap.session_status ?? ({} as SessionStatus);
  const gate = snap.runtime_gate ?? ({} as RuntimeGate);
  const threshold = num(snap.thresholds?.buy_score_threshold);
  const blockers = Array.isArray(ss.blockers) ? ss.blockers : [];
  const allBuyRows = snap.buy_rankings ?? [];
  const buyRows = selectPriorityBuyRows(allBuyRows);
  const positionRows = snap.sell_rankings ?? [];
  const positionCount = positionRows.length;
  const liquidityWarning = st(snap.position_risk?.liquidity_warning);
  const overnightRisk = st(snap.position_risk?.overnight_position_risk);

  return (
    <main className="min-h-screen space-y-4 bg-gray-950 p-3 text-gray-100 sm:p-4">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-400">R10</div>
          <h1 className="text-lg font-bold sm:text-xl">
            {tr(locale, 'Realtime Trading Cockpit', '实时交易驾驶舱')}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
          <span className={`inline-flex items-center gap-1.5 ${wsLive ? 'text-green-400' : 'text-gray-500'}`}>
            <span className={`h-2 w-2 rounded-full ${wsLive ? 'bg-green-500' : 'bg-gray-600'}`} />
            {wsLive ? tr(locale, 'Realtime', '实时') : tr(locale, 'Polling fallback', '轮询保障')}
          </span>
          <span>
            {tr(locale, 'Snapshot', '快照')} {fmtSyncTs(snap.as_of, lastFetch, locale)}
          </span>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/80 px-3 py-2 text-xs text-red-300">
          {error} · {tr(locale, 'showing last snapshot', '继续显示上一版快照')}
        </div>
      )}

      <section className="rounded-xl border border-gray-800 bg-gray-900 p-3 sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Badge label={tr(locale, 'Session', '时段')} value={ss.market_session} />
            <Badge label={tr(locale, 'Runtime', '运行')} value={ss.runtime_state} />
            <Badge label={tr(locale, 'Scoring', '评分')} value={ss.scoring_freeze_state} />
            <BoolBadge
              label={tr(locale, 'Live submit', '实盘提交')}
              value={Boolean(ss.live_submit_allowed)}
              locale={locale}
            />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            <span>{tr(locale, 'Feed', '行情')} {fmtAge(ss.feed_age_ms, locale)}</span>
            <span>{tr(locale, 'Score', '评分')} {fmtAge(ss.score_age_ms, locale)}</span>
          </div>
        </div>
        {blockers.length > 0 && (
          <div className="mt-3 break-all border-t border-red-900/70 pt-3 text-xs text-red-300">
            <span className="mr-2 font-semibold">{tr(locale, 'Blocked by', '当前阻断')}</span>
            {blockers.map(b => st(b)).join(' · ')}
          </div>
        )}
      </section>

      <ExtendedHoursPanel locale={locale} />

      <details className="group rounded-xl border border-gray-800 bg-gray-900">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-3 text-sm font-semibold text-gray-300 sm:px-4">
          <span>{tr(locale, 'Runtime diagnostics', '运行诊断')}</span>
          <span
            className="flex min-w-0 max-w-[68%] items-center gap-2 text-xs font-normal text-gray-500 sm:max-w-none"
            title={`${st(gate.runtime_state)} · ${st(gate.data_health_state)}`}
          >
            <span className="truncate text-right">
            {st(gate.runtime_state)} · {st(gate.data_health_state)}
            </span>
            <span className="transition-transform group-open:rotate-180">⌄</span>
          </span>
        </summary>
        <div className="space-y-3 border-t border-gray-800 px-3 py-3 sm:px-4">
          <div className="flex flex-wrap gap-2">
            <Badge label={tr(locale, 'Runtime', '运行')} value={gate.runtime_state} />
            <Badge label={tr(locale, 'Boot policy', '启动策略')} value={gate.boot_policy_state} />
            <Badge label={tr(locale, 'Desired', '期望模式')} value={gate.desired_mode} />
            <Badge label={tr(locale, 'Score feed', '评分流')} value={gate.score_feed_state} />
            <Badge label={tr(locale, 'Data health', '数据健康')} value={gate.data_health_state} />
          </div>
          <div className="flex flex-wrap gap-2">
            <BoolBadge label={tr(locale, 'Buy intent', '买入意图')} value={Boolean(gate.buy_intent_allowed)} locale={locale} />
            <BoolBadge label={tr(locale, 'Sell eval', '卖出评估')} value={Boolean(gate.sell_eval_allowed)} locale={locale} />
            <BoolBadge label={tr(locale, 'Sell submit', '卖出提交')} value={Boolean(gate.sell_submit_allowed)} locale={locale} />
          </div>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs text-gray-500 sm:grid-cols-2 xl:grid-cols-4">
            <div><dt>{tr(locale, 'Mode', '模式')}</dt><dd className="break-all text-gray-300">{st(snap.position_risk?.mode)}</dd></div>
            <div><dt>{tr(locale, 'Market source', '行情源')}</dt><dd className="break-all text-gray-300">{st(snap.market_data_source || ss.market_data_source)}</dd></div>
            <div><dt>{tr(locale, 'Score source', '评分源')}</dt><dd className="break-all text-gray-300">{st(snap.score_source || ss.score_source)}</dd></div>
            <div><dt>{tr(locale, 'Broker truth', '券商真相')}</dt><dd className="break-all text-gray-300">{st(snap.broker_meta?.source || snap.broker_truth_source)}</dd></div>
            <div><dt>{tr(locale, 'Broker sync', '券商同步')}</dt><dd className="text-gray-300">{fmtSyncTs(snap.broker_meta?.last_sync_at, lastFetch, locale)}</dd></div>
            <div><dt>{tr(locale, 'Snapshot', '快照')}</dt><dd className="text-gray-300">{fmtSyncTs(snap.as_of, lastFetch, locale)}</dd></div>
            <div><dt>{tr(locale, 'Reason', '原因')}</dt><dd className="break-all text-gray-300">{st(gate.reason_code || ss.reason_code)}</dd></div>
            <div><dt>{tr(locale, 'Session ID', '会话 ID')}</dt><dd className="break-all text-gray-300">{st(gate.runtime_session_id)}</dd></div>
          </dl>
        </div>
      </details>

      <section className="rounded-xl border border-gray-800 bg-gray-900 p-3 sm:p-4">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-bold text-gray-300">
            {tr(locale, 'Buy candidates', '买入候选')}
          </h2>
          <span className="text-right text-xs text-gray-500">
            {tr(locale, 'Priority', '重点')} {buyRows.length}/{allBuyRows.length} · {tr(locale, 'threshold', '阈值')} {threshold === null ? 'N/A' : threshold}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-800 text-left text-gray-500">
                <th className="hidden py-1 pr-2 md:table-cell">#</th>
                <th className="py-1 pr-3">{tr(locale, 'Symbol', '标的')}</th>
                <th className="py-1 pr-3 text-right">{tr(locale, 'Score', '评分')}</th>
                <th className="hidden py-1 pr-3 text-right sm:table-cell">{tr(locale, 'Price', '价格')}</th>
                <th className="py-1 pr-3 text-right whitespace-nowrap">{tr(locale, 'Chg%', '涨跌幅')}</th>
                <th className="hidden py-1 pr-3 text-right lg:table-cell">{tr(locale, 'RelVol', '量比')}</th>
                <th className="hidden py-1 pr-3 md:table-cell">{tr(locale, 'State', '状态')}</th>
                <th className="hidden py-1 md:table-cell">{tr(locale, 'Block', '阻断')}</th>
              </tr>
            </thead>
            <tbody>
              {buyRows.map(row => {
                const blockReason = st(row.block_reason);
                return (
                  <tr key={`${row.rank}-${row.symbol}`} className="border-b border-gray-800/80 hover:bg-gray-800/50">
                    <td className="hidden py-2 pr-2 text-gray-500 md:table-cell">{row.rank}</td>
                    <td className="py-2 pr-3 align-top font-bold">
                      {st(row.symbol)}
                      <div className="mt-1 flex flex-wrap items-center gap-1 md:hidden">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${stateTone(row.buy_state)}`}>
                          {st(row.buy_state)}
                        </span>
                        {blockReason !== 'NONE' && (
                          <span className="max-w-[13rem] break-all text-[10px] font-normal text-red-300">
                            {blockReason}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right align-top font-mono">{fmtNum(row.buy_score)}</td>
                    <td className="hidden py-2 pr-3 text-right align-top font-mono sm:table-cell">
                      {num(row.price) === null ? 'N/A' : `$${fmtNum(row.price)}`}
                    </td>
                    <td className={`py-2 pr-3 text-right align-top font-mono whitespace-nowrap ${
                      num(row.change_pct) === null ? 'text-gray-600' : (row.change_pct as number) >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {fmtPct(row.change_pct)}
                    </td>
                    <td className="hidden py-2 pr-3 text-right align-top font-mono text-gray-400 lg:table-cell">
                      {fmtNum(row.relative_volume)}
                    </td>
                    <td className="hidden py-2 pr-3 align-top md:table-cell">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${stateTone(row.buy_state)}`}>
                        {st(row.buy_state)}
                      </span>
                    </td>
                    <td className="hidden max-w-xs break-all py-2 align-top text-gray-500 md:table-cell">
                      {blockReason === 'NONE' ? '—' : blockReason}
                    </td>
                  </tr>
                );
              })}
              {buyRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-4 text-center text-gray-600">
                    {tr(locale, 'No candidates', '暂无候选')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-gray-800 bg-gray-900 p-3 sm:p-4">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-bold text-gray-300">
            {tr(locale, 'Positions · broker truth', '持仓 · 券商真相')}
          </h2>
          <span className="max-w-[65%] break-all text-right text-xs text-gray-500">
            {st(snap.position_risk?.mode)} · {positionCount}
          </span>
        </div>
        {(liquidityWarning !== 'NONE' || overnightRisk !== 'NONE') && (
          <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 border-b border-amber-900/60 pb-3 text-xs text-amber-300">
            {liquidityWarning !== 'NONE' && (
              <span>{tr(locale, 'Liquidity', '流动性')}: {liquidityWarning}</span>
            )}
            {overnightRisk !== 'NONE' && (
              <span>{tr(locale, 'Overnight', '隔夜风险')}: {overnightRisk}</span>
            )}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-800 text-left text-gray-500">
                <th className="py-1 pr-3">{tr(locale, 'Symbol', '标的')}</th>
                <th className="hidden py-1 pr-3 text-right sm:table-cell">{tr(locale, 'Qty', '数量')}</th>
                <th className="hidden py-1 pr-3 text-right lg:table-cell">{tr(locale, 'Avg cost', '成本')}</th>
                <th className="hidden py-1 pr-3 text-right md:table-cell">{tr(locale, 'Last', '现价')}</th>
                <th className="py-1 pr-3 text-right">{tr(locale, 'UPnL', '浮动盈亏')}</th>
                <th className="hidden py-1 pr-3 text-right sm:table-cell">UPnL%</th>
                <th className="hidden py-1 md:table-cell">{tr(locale, 'State', '状态')}</th>
              </tr>
            </thead>
            <tbody>
              {positionRows.map(row => {
                const pnl = num(row.unrealized_pnl);
                const pnlTone = pnl === null ? 'text-gray-600' : pnl >= 0 ? 'text-green-400' : 'text-red-400';
                return (
                  <tr key={`${row.rank}-${row.symbol}`} className="border-b border-gray-800/80 hover:bg-gray-800/50">
                    <td className="py-2 pr-3 align-top font-bold">
                      {st(row.symbol)}
                      <div className="mt-1 flex flex-wrap items-center gap-1 md:hidden">
                        <span className="text-[10px] font-normal text-gray-500">
                          {tr(locale, 'Qty', '数量')} {fmtNum(row.position_qty, 0)}
                        </span>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${stateTone(row.sell_state)}`}>
                          {st(row.sell_state)}
                        </span>
                      </div>
                    </td>
                    <td className="hidden py-2 pr-3 text-right align-top font-mono sm:table-cell">{fmtNum(row.position_qty, 0)}</td>
                    <td className="hidden py-2 pr-3 text-right align-top font-mono lg:table-cell">
                      {num(row.avg_cost) === null ? 'N/A' : `$${fmtNum(row.avg_cost)}`}
                    </td>
                    <td className="hidden py-2 pr-3 text-right align-top font-mono md:table-cell">
                      {num(row.last_price) === null ? 'N/A' : `$${fmtNum(row.last_price)}`}
                    </td>
                    <td className={`py-2 pr-3 text-right align-top font-mono ${pnlTone}`}>
                      {pnl === null ? 'N/A' : `$${fmtNum(row.unrealized_pnl)}`}
                      <div className="text-[10px] sm:hidden">{fmtPct(row.unrealized_pnl_pct)}</div>
                    </td>
                    <td className={`hidden py-2 pr-3 text-right align-top font-mono sm:table-cell ${pnlTone}`}>
                      {fmtPct(row.unrealized_pnl_pct)}
                    </td>
                    <td className="hidden py-2 align-top md:table-cell">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${stateTone(row.sell_state)}`}>
                        {st(row.sell_state)}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {positionRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-4 text-center text-gray-600">
                    {tr(locale, 'No positions', '暂无持仓')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
